/**
 * VFX Emitter Component
 *
 * Particle system emitter that plays VFX graphs.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  executeInEditor,
  field,
  customEditor,
} from "../attributes";
import { EngineComponent } from "../scripting/EngineComponent";

@component("core.VFXEmitter")
@customEditor("VFXEmitterEditor")
@displayName("VFX Emitter")
@icon("Sparkles")
@color("#a29bfe")
@category("Effects")
@allowMultiple(true)
@requireComponent(["core.Transform"])
@executeInEditor(false)
export class VFXEmitter extends EngineComponent {
  @field({ type: "asset", assetType: "vfx", label: "VFX Graph", tooltip: "Drag a .vfx file here" })
  vfxAsset: string = "";

  @field({ type: "bool", label: "Play On Awake" })
  playOnAwake: boolean = true;

  @field({ type: "bool", label: "Loop" })
  loop: boolean = false;

  @field({ type: "bool", label: "Die On End", tooltip: "Destroy the entity when all particles have died (non-looping only)" })
  dieOnEnd: boolean = false;

  @field({ type: "bool", label: "Prewarm", tooltip: "Simulate particles before first frame" })
  prewarm: boolean = false;

  @field({ type: "f32", label: "Playback Speed", min: 0.1, max: 10 })
  playbackSpeed: number = 1;

  @field({ type: "i32", label: "Max Particles", tooltip: "0 = use graph default", min: 0, max: 100000 })
  maxParticlesOverride: number = 0;

  @field({ type: "i32", label: "Sorting Layer" })
  sortingLayer: number = 1;

  @field({ type: "i32", label: "Order in Layer" })
  orderInLayer: number = 0;

  @field({ type: "object", hidden: true })
  variableOverrides: Record<string, unknown> = {};

  // ── Runtime API (Unity-style; proxies to engine.vfx for the bound entity) ──
  // Not @field → not serialized. The live emitter is owned by the VFX subsystem and resolved
  // per call by entity→GUID; nothing native is stored here.
  // Usage: `this.getComponent(Components.VFXEmitter)?.play()`.

  /** Start/resume this emitter. */
  play(): void {
    this.engine?.vfx?.play?.(this.entity);
  }

  /** Pause this emitter. */
  stop(): void {
    this.engine?.vfx?.stop?.(this.entity);
  }

  /** Manually emit `count` particles. */
  spawn(count: number): void {
    this.engine?.vfx?.spawn?.(this.entity, count);
  }

  /** Toggle loop mode. */
  setLoop(on: boolean): void {
    this.engine?.vfx?.setLoop?.(this.entity, on);
  }

  /** Whether the emitter has finished (false when no emitter/subsystem). */
  get isComplete(): boolean {
    return this.engine?.vfx?.isComplete?.(this.entity) ?? false;
  }
}
